import React from 'react';
import Logo from './Logo';
import FadeInOnScroll from '../shared/FadeInOnScroll';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TWILIGHT_NAVY, IRIS_PURPLE, SHELL_BEIGE, alpha } from '../../theme/colors';
import Label from '../shared/Label';

// Same array-of-lines shape as the pre-rebuild AuthScreen.js LETTER_LINES:
// '' marks a paragraph break, consecutive non-empty lines render as
// separate <br/>-joined lines within one paragraph block.
const LETTER_LINES = [
  "\"For most of our lives, we've been taught to think about health like a report card — a number on a scale, a streak in an app, a goal we either hit or miss.",
  '',
  'But your body was never meant to be reduced to a single score.',
  '',
  'As women, our bodies are constantly adjusting — responding to sleep, nourishment, training, stress, and the natural rhythms of our hormones. What feels easy one week may feel harder the next. Energy rises and falls. Recovery comes in waves.',
  '',
  'The modern world asks us to ignore these signals.',
  'To push through them.',
  'To measure ourselves against a static ideal.',
  '',
  'But healing begins when we start listening again.',
  'When we become curious about what our bodies are telling us. When we see patterns where we once saw frustration.',
  '',
  'I created Vesna to help us do exactly that.',
  '',
  'Instead of scoring your health, Vesna helps you observe it. Instead of judging your habits, it helps you understand how your body responds to them.',
  '',
  'Over time, something powerful happens.',
  'You begin to recognize the rhythms of your own physiology.',
  '',
  'Your body already knows how to guide you. Vesna simply helps you see the patterns."',
];

// Groups consecutive non-empty lines into paragraphs (joined by <br/>),
// splitting on '' — mirrors how the mobile Paper frame already had this
// laid out as separate paragraph blocks.
const PARAGRAPHS = LETTER_LINES.reduce((acc, line) => {
  if (line === '') {
    acc.push([]);
  } else {
    if (acc.length === 0) acc.push([]);
    acc[acc.length - 1].push(line);
  }
  return acc;
}, []).filter((p) => p.length > 0);

// Per-paragraph via FadeInOnScroll (same IntersectionObserver reveal used
// for the letter in the pre-rebuild AuthScreen.js and throughout
// ExploreMyPatternsModal) — no delay prop, same as that implementation.
// A per-paragraph delay that cycles by index (e.g. i % 4) breaks the
// continuous top-to-bottom reveal: whenever two paragraphs cross the
// visibility threshold in the same scroll tick, the one further down can
// get a shorter delay than the one above it and pop in first. Scroll
// position alone already sequences them correctly; a manual delay only
// fights that.
const Letter = ({ fontSize, lineHeight, marginBottom }) => (
  <>
    {PARAGRAPHS.map((lines, i) => (
      <FadeInOnScroll key={i}>
        <p
          style={{
            fontFamily: 'Instrument Serif',
            fontSize,
            lineHeight,
            color: TWILIGHT_NAVY,
            marginBottom: i === PARAGRAPHS.length - 1 ? 0 : marginBottom,
          }}
        >
          {lines.map((line, j) => (
            <React.Fragment key={j}>
              {j > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </p>
      </FadeInOnScroll>
    ))}
  </>
);

const Signature = ({ fontSize, marginTop }) => (
  <FadeInOnScroll>
    <div style={{ display: 'flex', flexDirection: 'column', marginTop }}>
      <span style={{ fontFamily: 'Instrument Serif', fontStyle: 'italic', fontSize, lineHeight: 1.3, color: IRIS_PURPLE }}>with love,</span>
      <span style={{ fontFamily: 'Instrument Serif', fontStyle: 'italic', fontSize, lineHeight: 1.3, color: IRIS_PURPLE }}>ally</span>
    </div>
  </FadeInOnScroll>
);

const FounderPhoto = ({ width, height, captionSize }) => (
  <div
    style={{
      position: 'relative',
      flexShrink: 0,
      width,
      height,
      borderRadius: width > 350 ? 24 : 20,
      overflow: 'hidden',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'flex-end',
      padding: width > 350 ? 24 : 20,
      backgroundImage: 'url(/landing-assets/founder.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: '50% 20%',
    }}
  >
    <span style={{ fontFamily: 'Instrument Serif', fontStyle: 'italic', fontSize: captionSize, color: alpha('white', 0.8) }}>
      Ally Tumasova, creator of Vesna
    </span>
  </div>
);

const FooterBottom = ({ isMobile }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      rowGap: 10,
      width: '100%',
      marginTop: isMobile ? 56 : 80,
      paddingTop: isMobile ? 20 : 24,
      borderTop: `1px solid ${alpha('navy', 0.1)}`,
    }}
  >
    <Logo size={isMobile ? 16 : 18} textSize={isMobile ? 16 : 18} color={alpha('navy', 0.5)} textColor={alpha('navy', 0.7)} />
    <Label muted as="a" href="mailto:hi@vesna.health" style={{ fontFamily: 'DM Sans', fontSize: isMobile ? 11 : 13, textDecoration: 'none' }}>
      hi@vesna.health
    </Label>
    <Label muted style={{ fontFamily: 'DM Sans', fontSize: isMobile ? 11 : 13 }}>© 2026 BUDMO LLC</Label>
  </div>
);

const Footer = () => {
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SHELL_BEIGE,
        padding: isMobile ? '56px 20px 32px' : '120px 64px 100px',
      }}
    >
      <span
        style={{
          alignSelf: 'center',
          textAlign: 'center',
          fontFamily: 'DM Sans',
          fontSize: isMobile ? 10 : 12,
          fontWeight: 600,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: IRIS_PURPLE,
        }}
      >
        Built with love in NYC
      </span>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: 28 }}>
          <Letter fontSize={17} lineHeight="155%" marginBottom={18} />
          <Signature fontSize={16} marginTop={24} />
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>
            <FounderPhoto width={335} height={410} captionSize={13} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 34, marginTop: 44, width: '100%' }}>
          <FounderPhoto width={360} height={440} captionSize={13} />
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Letter fontSize={24} lineHeight="150%" marginBottom={20} />
            <Signature fontSize={17} marginTop={28} />
          </div>
        </div>
      )}

      <FooterBottom isMobile={isMobile} />
    </div>
  );
};

export default Footer;
